import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Sparkles,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Plus,
  Calendar,
  X,
  Check,
  Loader2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";

const TABS = [
  { key: "devotionals", label: "Devocionais", icon: BookOpen },
  { key: "verses", label: "Versículos", icon: BookOpen },
  { key: "phrases", label: "Frases", icon: Sparkles },
];

const FIELDS_BY_TYPE = {
  devotionals: [
    { name: "title", label: "Título", type: "text", required: true },
    { name: "verse_reference", label: "Versículo base", type: "text", required: true },
    { name: "verse_text", label: "Texto do versículo", type: "textarea", rows: 3, required: true },
    { name: "reflection", label: "Reflexão", type: "textarea", rows: 8, required: true },
    { name: "application", label: "Aplicação prática", type: "textarea", rows: 3, required: true },
    { name: "prayer", label: "Oração", type: "textarea", rows: 3, required: true },
    { name: "impact_phrase", label: "Frase de impacto", type: "text" },
  ],
  verses: [
    { name: "reference", label: "Referência (ex: Salmos 46:10)", type: "text", required: true },
    { name: "text", label: "Texto do versículo", type: "textarea", rows: 4, required: true },
    { name: "translation", label: "Tradução (ARA, NVI, NTLH...)", type: "text" },
  ],
  phrases: [
    { name: "phrase", label: "Frase", type: "textarea", rows: 3, required: true },
    { name: "reference", label: "Referência bíblica (opcional)", type: "text" },
    { name: "category", label: "Categoria", type: "text" },
  ],
};

function titleOf(type, item) {
  if (type === "devotionals") return item.title;
  if (type === "verses") return item.reference;
  if (type === "phrases") return item.phrase;
  return "";
}

function subtitleOf(type, item) {
  if (type === "devotionals") return item.verse_reference;
  if (type === "verses") return item.text;
  if (type === "phrases") return item.reference || item.category;
  return "";
}

export default function AdminDailyContent() {
  const [tab, setTab] = useState("devotionals");
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited, or "new"
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const loadPreview = useCallback(() => {
    api
      .get("/admin/daily/preview")
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null));
  }, []);

  const loadItems = useCallback(
    (t = tab) => {
      setLoading(true);
      api
        .get(`/admin/daily/${t}`)
        .then(({ data }) => setItems(data || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    },
    [tab]
  );

  useEffect(() => {
    loadItems(tab);
    loadPreview();
  }, [tab, loadItems, loadPreview]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/admin/daily/${tab}/${editing.id}`, payload);
        showToast("Alterações salvas.");
      } else {
        await api.post(`/admin/daily/${tab}`, payload);
        showToast("Item criado com sucesso.");
      }
      setEditing(null);
      loadItems(tab);
      loadPreview();
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Erro ao salvar. Verifique os campos.",
        false
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir "${titleOf(tab, item).slice(0, 60)}"?`)) return;
    try {
      await api.delete(`/admin/daily/${tab}/${item.id}`);
      showToast("Item excluído.");
      loadItems(tab);
      loadPreview();
    } catch (e) {
      showToast("Erro ao excluir.", false);
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      await api.patch(`/admin/daily/${tab}/${item.id}/publish`);
      loadItems(tab);
      loadPreview();
    } catch (e) {
      showToast("Erro ao publicar/despublicar.", false);
    }
  };

  const handleReorder = async (item, direction) => {
    const currentIndex = items.findIndex((i) => i.id === item.id);
    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const reordered = [...items];
    [reordered[currentIndex], reordered[newIndex]] = [
      reordered[newIndex],
      reordered[currentIndex],
    ];
    const payload = {
      items: reordered.map((it, idx) => ({ id: it.id, order: idx })),
    };
    setItems(reordered); // optimistic
    try {
      await api.put(`/admin/daily/${tab}/reorder`, payload);
      loadItems(tab);
      loadPreview();
    } catch {
      showToast("Erro ao reordenar.", false);
      loadItems(tab);
    }
  };

  return (
    <section
      data-testid="admin-daily-content"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between gap-3 mb-8">
        <div className="flex items-center gap-3 text-[#C5A059]">
          <Calendar size={16} strokeWidth={1.5} />
          <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
            Conteúdo Diário · rotação automática
          </h2>
        </div>
        <span className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/50">
          Troca por data · horário de Brasília
        </span>
      </div>

      {/* Preview hoje/amanhã */}
      {preview && (
        <div
          data-testid="daily-preview"
          className="grid md:grid-cols-2 gap-4 mb-10"
        >
          <PreviewCard
            label={`Hoje · ${preview.br_date_today}`}
            items={preview.today}
          />
          <PreviewCard
            label={`Amanhã · ${preview.br_date_tomorrow}`}
            items={preview.tomorrow}
            muted
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#0B1E36]/10 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const count = preview?.counts?.[t.key] ?? "·";
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`daily-tab-${t.key}`}
              className={`px-4 py-3 text-[0.72rem] uppercase tracking-[0.22em] font-medium flex items-center gap-2 transition-colors duration-200 border-b-2 ${
                active
                  ? "text-[#0B1E36] border-[#C5A059]"
                  : "text-[#6B7280] border-transparent hover:text-[#0B1E36]"
              }`}
            >
              <Icon size={14} strokeWidth={1.5} />
              {t.label}
              <span className="text-[0.65rem] text-[#C5A059] ml-1">
                · {count}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setEditing({})}
          data-testid="daily-new-button"
          className="flex items-center gap-2 bg-[#0B1E36] hover:bg-[#13294B] text-white text-[0.7rem] uppercase tracking-[0.22em] font-medium px-4 py-2.5 mb-1 transition-colors duration-200"
        >
          <Plus size={14} strokeWidth={1.7} />
          Novo
        </button>
      </div>

      {/* Items list */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#6B7280]">
          <Loader2 size={18} className="animate-spin mr-2" />
          Carregando…
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-[#6B7280]">
          Nenhum item cadastrado ainda. Clique em{" "}
          <span className="font-medium text-[#0B1E36]">Novo</span> para começar.
        </div>
      ) : (
        <ul data-testid="daily-items-list" className="divide-y divide-[#0B1E36]/8">
          {items.map((item, idx) => (
            <ItemRow
              key={item.id}
              item={item}
              index={idx}
              total={items.length}
              type={tab}
              onEdit={() => setEditing(item)}
              onDelete={() => handleDelete(item)}
              onTogglePublish={() => handleTogglePublish(item)}
              onReorder={(dir) => handleReorder(item, dir)}
            />
          ))}
        </ul>
      )}

      {/* Edit modal */}
      {editing !== null && (
        <EditModal
          type={tab}
          item={editing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="daily-toast"
          className={`fixed bottom-6 right-6 px-5 py-3 text-sm shadow-xl z-[70] ${
            toast.ok ? "bg-[#0B1E36] text-white" : "bg-[#B91C1C] text-white"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}

function PreviewCard({ label, items, muted = false }) {
  const opacity = muted ? "opacity-70" : "";
  return (
    <div
      className={`bg-[#F5F7FA] border border-[#0B1E36]/10 p-5 ${opacity}`}
    >
      <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium mb-3">
        {label}
      </p>
      <div className="space-y-2.5">
        {[
          ["Devocional", items?.devotionals?.title],
          ["Versículo", items?.verses?.reference],
          ["Frase", items?.phrases?.phrase],
        ].map(([label, value]) => (
          <div key={label} className="flex gap-3 text-[0.82rem]">
            <span className="text-[0.62rem] uppercase tracking-[0.2em] text-[#0B1E36]/55 w-20 flex-shrink-0 pt-0.5">
              {label}
            </span>
            <span className="text-[#0B1E36] truncate">
              {value || <em className="text-[#6B7280]">sem item</em>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemRow({
  item,
  index,
  total,
  type,
  onEdit,
  onDelete,
  onTogglePublish,
  onReorder,
}) {
  return (
    <li className="flex items-start gap-4 py-4">
      <div className="flex-shrink-0 w-10 pt-1 text-center font-serif-display text-[#C5A059] tabular-nums">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[0.92rem] text-[#0B1E36] font-medium truncate">
          {titleOf(type, item)}
          {!item.published && (
            <span className="ml-2 text-[0.6rem] uppercase tracking-[0.2em] bg-[#6B7280]/15 text-[#6B7280] px-2 py-0.5 rounded">
              Despublicado
            </span>
          )}
        </p>
        <p className="mt-1 text-[0.78rem] text-[#6B7280] truncate">
          {subtitleOf(type, item)}
        </p>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <IconBtn
          onClick={() => onReorder(-1)}
          disabled={index === 0}
          label="Mover acima"
        >
          <ChevronUp size={14} strokeWidth={1.7} />
        </IconBtn>
        <IconBtn
          onClick={() => onReorder(1)}
          disabled={index === total - 1}
          label="Mover abaixo"
        >
          <ChevronDown size={14} strokeWidth={1.7} />
        </IconBtn>
        <IconBtn
          onClick={onTogglePublish}
          label={item.published ? "Despublicar" : "Publicar"}
          testid={`daily-toggle-${item.id}`}
        >
          {item.published ? (
            <Eye size={14} strokeWidth={1.7} />
          ) : (
            <EyeOff size={14} strokeWidth={1.7} className="text-[#6B7280]" />
          )}
        </IconBtn>
        <IconBtn
          onClick={onEdit}
          label="Editar"
          testid={`daily-edit-${item.id}`}
        >
          <Pencil size={14} strokeWidth={1.7} />
        </IconBtn>
        <IconBtn
          onClick={onDelete}
          label="Excluir"
          testid={`daily-delete-${item.id}`}
        >
          <Trash2 size={14} strokeWidth={1.7} className="text-[#B91C1C]" />
        </IconBtn>
      </div>
    </li>
  );
}

function IconBtn({ children, onClick, disabled, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="w-8 h-8 flex items-center justify-center text-[#0B1E36] hover:bg-[#F5F7FA] disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
    >
      {children}
    </button>
  );
}

function EditModal({ type, item, onCancel, onSave, saving }) {
  const fields = FIELDS_BY_TYPE[type] || [];
  const isNew = !item.id;
  const [form, setForm] = useState(() => {
    const initial = {};
    for (const f of fields) initial[f.name] = item[f.name] ?? "";
    initial.published = item.published ?? true;
    return initial;
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Clean empty optional strings to null where it makes sense
    for (const f of fields) {
      if (!f.required && typeof payload[f.name] === "string" && payload[f.name].trim() === "") {
        if (f.name === "reference" || f.name === "category" || f.name === "translation" || f.name === "impact_phrase") {
          // preserve as empty string / null where appropriate
          if (f.name === "reference") payload[f.name] = null;
        }
      }
    }
    onSave(payload);
  };

  return (
    <div
      data-testid="daily-edit-modal"
      className="fixed inset-0 z-[80] flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden />
      <div className="relative bg-white w-full max-w-2xl shadow-2xl my-auto">
        <div className="flex items-center justify-between border-b border-[#0B1E36]/10 px-6 py-4">
          <h3 className="font-serif-display text-[1.25rem] text-[#0B1E36]">
            {isNew ? "Novo item" : "Editar item"}
            <span className="ml-2 text-[0.65rem] uppercase tracking-[0.24em] text-[#C5A059]">
              {type}
            </span>
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fechar"
            className="w-8 h-8 flex items-center justify-center text-[#6B7280] hover:text-[#0B1E36] transition-colors"
          >
            <X size={16} strokeWidth={1.7} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-5">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[0.65rem] uppercase tracking-[0.24em] text-[#0B1E36]/60 mb-2 font-medium">
                {f.label}
                {f.required && <span className="text-[#C5A059] ml-1">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  value={form[f.name] || ""}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  rows={f.rows || 4}
                  required={f.required}
                  data-testid={`daily-field-${f.name}`}
                  className="w-full px-3 py-2.5 border border-[#0B1E36]/15 focus:border-[#C5A059] focus:outline-none text-[0.92rem] text-[#0B1E36] bg-white resize-y leading-relaxed"
                />
              ) : (
                <input
                  type="text"
                  value={form[f.name] || ""}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  data-testid={`daily-field-${f.name}`}
                  className="w-full px-3 py-2.5 border border-[#0B1E36]/15 focus:border-[#C5A059] focus:outline-none text-[0.92rem] text-[#0B1E36] bg-white"
                />
              )}
            </div>
          ))}
          <label className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              checked={!!form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="daily-field-published"
              className="w-4 h-4 accent-[#C5A059]"
            />
            <span className="text-[0.82rem] text-[#0B1E36]">
              Publicado (entra na rotação diária)
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#0B1E36]/10">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] text-[#6B7280] hover:text-[#0B1E36] transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="daily-save-button"
              className="flex items-center gap-2 bg-[#C5A059] hover:bg-[#D4B26B] disabled:opacity-50 text-[#0B1E36] px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] font-medium transition-colors"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} strokeWidth={1.7} />
              )}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
